import { useEffect, useMemo, useState } from 'react';
import { educationMaterials, searchMaterials, type EducationMaterial } from '../textures/educationMaterials';
import { loadEducationPack, prepareEducationAssets } from '../textures/educationPack';
import { materialDataUrl } from '../textures/preview';
import type { RawResourcePack } from '../textures/resourcePack';
import { InventoryHint } from './InventoryHint';
import { InventoryMapButton } from './InventoryMap';

const PAGE_SIZE = 24;

/** Search the Education inventory even when an item is absent from the building. */
export function MaterialCatalog({ rawPack }: { rawPack: RawResourcePack | null }) {
  const [open, setOpen] = useState(false);
  const [materials, setMaterials] = useState<EducationMaterial[]>([]);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | EducationMaterial['kind']>('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [thumbnails, setThumbnails] = useState(new Map<string, string>());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    void loadEducationPack().then(pack => {
      if (!cancelled) setMaterials(educationMaterials(pack.registry));
    }).catch(() => {
      if (!cancelled) setError('材料一覧を読み込めませんでした。一度閉じて、もう一度ひらいてください。');
    });
    return () => { cancelled = true; };
  }, [open]);

  const matches = useMemo(() => searchMaterials(materials, query, kind), [materials, query, kind]);
  const visible = useMemo(() => matches.slice(0, limit), [matches, limit]);
  useEffect(() => {
    if (!open || !visible.length) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setThumbnails(new Map());
    void prepareEducationAssets([], rawPack, visible.map(item => item.id)).then(pack => {
      if (cancelled) return;
      const images = new Map<string, string>();
      for (const item of visible) {
        const image = materialDataUrl(item.id, pack);
        if (image) images.set(item.id, image);
      }
      setThumbnails(images);
      setError(null);
    }).catch(() => {
      if (!cancelled) setError('画像を読み込めませんでした。名前と検索案内を使ってください。');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, visible, rawPack]);

  return (
    <details className="material-catalog" onToggle={event => setOpen(event.currentTarget.open)}>
      <summary>材料を探す（ブロック・アイテム）</summary>
      {open && <div className="material-catalog-body">
        <p>望遠鏡や道具も探せます。建築に使われていない材料も表示します。</p>
        <label htmlFor="material-search">材料の名前・ID</label>
        <input id="material-search" type="search" placeholder="例：望遠鏡、spyglass" value={query} onChange={event => { setQuery(event.target.value); setLimit(PAGE_SIZE); }} />
        <label htmlFor="material-kind">種類</label>
        <select id="material-kind" value={kind} onChange={event => { setKind(event.target.value as typeof kind); setLimit(PAGE_SIZE); }}>
          <option value="all">すべて</option>
          <option value="block">ブロック</option>
          <option value="item">アイテム・道具</option>
        </select>
        {error && <p role="alert">{error}</p>}
        <p role="status">{materials.length ? `${matches.length.toLocaleString()}件${matches.length > limit ? `（${limit}件表示）` : ''}` : error ? '' : '材料を読み込んでいます…'}</p>
        {materials.length > 0 && matches.length === 0 && <p>見つかりませんでした。短い名前や英語のIDでも探せます。</p>}
        <ul className="material-results" aria-busy={loading}>
          {visible.map(item => <li key={item.id}>
            {thumbnails.has(item.id)
              ? <img src={thumbnails.get(item.id)} alt="" width={44} height={44} />
              : <span className="material-image-placeholder">{loading ? '…' : '画像なし'}</span>}
            <div>
              <strong>{item.name}</strong>
              <span className="material-kind">{item.kind === 'block' ? 'ブロック' : 'アイテム'}</span>
              <small>{item.id}</small>
              <InventoryHint block={item.id} compact />
              <InventoryMapButton block={item.id} />
            </div>
          </li>)}
        </ul>
        {matches.length > limit && <button type="button" onClick={() => setLimit(value => value + PAGE_SIZE)}>もっと見る</button>}
      </div>}
    </details>
  );
}
